/**
 * Automatize the grid reporting
 *
 * <CheckListTable
 *    grid={grid}
 *    display={display}
 *    checks={checks}
 *    onPageChange={onPageChange}
 * />
 *
 * reference
 *  grid = {
 *    page: (integer),
 *    pageSize: (integer),
 *    total: (integer),
 *    items: [array of objects],
 *  }
 *  display = {
 *    id: 'Codigo',
 *    description: {header: 'Descripción', render: callback(row){ return ...}}
 *  }
 *  checks = {
 *    checkId: (string)
 *    selectedItems: [array of objects],
 *  }
 *  onPageChange = function(){...}
 *  labelLoading = (string) "Cargando datos..."
 *  labelNoData  = (string) "No hay datos"
 *  labelButtons = {object} check LABEL_BUTTONS
 */
import _ from "lodash";
import React, { Component } from "react";
// material
import {
    Table,
    TableBody,
    TableHeader,
    TableHeaderColumn,
    TableRow,
    TableRowColumn
} from 'material-ui/Table'
// pagination
import Pagination from "rc-pagination";
import "rc-pagination/assets/index.css";
import CheckItem from "./CheckItem"

// default label for buttons
const LABEL_BUTTONS = {
  prev_page: "Página anterior",
  next_page: "Página siguiente",
  prev_5: "5 páginas previas",
  next_5: "5 páginas siguientes",
  prev_3: "3 páginas previas",
  next_3: "3 páginas siguientes"
}

const defaultPageSize = 10;

class CheckListTable extends Component {

  constructor(props){
    super(props);
    // default configuration
    this.labelLoading = "Cargando datos...";
    this.labelNoData  = "No hay datos";
    this.labelButtons = LABEL_BUTTONS;
    this.pageSize = defaultPageSize;
    this.total = 0;
    this.checkId = null;
    this.items = [];
    this.state = {
      page: 1,
      items: [],
      selectedItems: {},
      selectAllItems: null
    }
    this.isFirstTime = true;
    this.onChangePage = this.onChangePage.bind(this);
    this.renderCheckItem = this.renderCheckItem.bind(this);
    this._initChechListProps = this._initChechListProps.bind(this);
  }

  /*
   * Change default configuration & initialize grid settings
   */
  componentWillReceiveProps(nextProps){
    let { grid, checks, labelLoading, labelNoData, labelButtons } = nextProps;
    // init label vars
    if(!_.isEmpty(labelLoading)){
      this.labelLoading = labelLoading;
    }
    if(!_.isEmpty(labelNoData)){
      this.labelNoData = labelNoData;
    }
    if(!_.isEmpty(labelButtons)){
      this.labelButtons = Object.assign({}, LABEL_BUTTONS, labelButtons);
    }
    // parse table vars
    let {page=1, pageSize=0, total=0, items} = grid;
    if (_.isArray(items)){
      this.items = items;
    }
    total = parseInt(total, 10);
    this.total = total<1? items.length : total;
    pageSize = parseInt(pageSize, 10);
    this.pageSize = pageSize>0? pageSize : defaultPageSize;
    // init checklist vars
    let { checkId, selectedItems=[], selectAllItems=null } = checks;
    this.checkId = checkId;
    if (this.isFirstTime && checkId!==undefined && _.size(items)) {
      // init component for the first time
      let newSelectedItems = this._initChechListProps({
        selectedItems,
        checkId,
        selectAllItems,
        itemLength: items.length
      });
      this.isFirstTime = false;
      this.onChangeCheckList(newSelectedItems);
    }
    // page oddities
    if(items.length === 0 && this.total>0){
      this.onChangePage(1);
      return;
    }
    if(this.pageSize * this.state.page >= this.total){
      this.onChangePage(1);
      return;
    }
    // page handling
    page = parseInt(page, 10);
    if (page<=1) {
      return;
    }
    if(this.pageSize * page < this.total){
      this.setState({page})
    } else {
      this.onChangePage(1);
    }
  }

  _initChechListProps({selectedItems, checkId, selectAllItems, itemLength}){
    let newSelectedItems = {};
    // should check all?
    if(selectAllItems === true){
      this.onChangeCheckBox('all', true);
      return this.getAllSelectedItem(true);
    }
    // should uncheck all?
    if(selectAllItems === false){
      this.onChangeCheckBox('all', false);
      return newSelectedItems;
    }
    if(_.size(selectedItems)===0) return newSelectedItems;
    for(let i in selectedItems){
      let id = selectedItems[i][checkId];
      newSelectedItems[id] = true;
      this.onChangeCheckBox(id, true);
    }
    // should init checkAll checkbox?
    const size = _.size(newSelectedItems);
    if(size>0 && size === itemLength){
      this.onChangeCheckBox('all', true);
      this.setState({selectedItems:newSelectedItems, selectAllItems:true});
    }
    return newSelectedItems;
  }

  ////////////////////////////////////////////////////////////////////////////////

  /**
   * OnChange checkbox event
   */
  onChangeCheckBox(id, checked) {
    let selectedItems = {};
    let flagCheckAll = null;
    // check/uncheck all items?
    if (id === "all") {
      selectedItems = this.getAllSelectedItem(checked);
      flagCheckAll = checked? true : false;
    } else {
      // add/remove single item
      selectedItems = checked? this.addNewSelectedItem(id) : this.removeSelectedItem(id);
      let countItems = _.size(selectedItems);
      if(countItems === 0){
        flagCheckAll = false;
      }
      if(countItems>0 && countItems === this.items.length){
        flagCheckAll = true;
      }
    }
    // set state
    this.setState({
      selectedItems: selectedItems,
      selectAllItems: flagCheckAll
    });
    this.onChangeCheckList(selectedItems);
  }

  onChangeCheckList(selectedItems){
    if(this.isFirstTime) return;
    const { onChangeCheckList } = this.props.checks;
    if(_.isFunction(onChangeCheckList)){
      //recreate selected item array
      let arraySelectedItems = [];
      for(let i in this.items){
        let id = this.items[i][this.checkId];
        if(selectedItems[id]){
          // eureka!
          arraySelectedItems.push(this.items[i]);
        }
      }
      onChangeCheckList(arraySelectedItems);
    }
  }

  /**
   * Check/uncheck all items from state
   */
  getAllSelectedItem(checked){
    checked = !! checked;
    let selectedItems = {};
    // check all items ?
    if(checked){
      for(let i in this.items){
        let id = this.items[i][this.checkId];
        selectedItems[id] = true;
      }
    }
    return selectedItems;
  }

  /**
   * add new check into state
   */
  addNewSelectedItem(id){
    let selectedItems = this.state.selectedItems;
    selectedItems[id] = true;
    return selectedItems;
  }

  /**
   * remove check from state
   */
  removeSelectedItem(id){
    let selectedItems = this.state.selectedItems;
    delete(selectedItems[id]);
    return selectedItems;
  }

  renderCheckItem(id, checked){
    // init vars...
    const { disabled } = this.props;
    const { selectAllItems } = this.state;
    const onChangeCheckBox = this.onChangeCheckBox.bind(this);
    const key = 'check_' + id;
    return(
      <CheckItem
        key={key}
        checkboxClassname="single-half-check"
        selectAllItems = {selectAllItems}
        disabled={disabled}
        checked={!!checked}
        name={id}
        label={null}
        onChange={onChangeCheckBox}
      />
    )
  }

  ////////////////////////////////////////////////////////////////////////////////

  /*
   * Return records for single page
   */
  getPageRows(){
    // local pagination?
    if(this.items.length>this.pageSize){
      // pages logically start with 1, but technically with 0
      let from = (this.state.page - 1) * this.pageSize;
      let to = (this.state.page) * this.pageSize;
      return this.items.slice(from, to);
    }
    // server pagination
    return this.items;
  }

  /*
   * Run on every page change event
   */
  onChangePage(page){
    if(_.has(this.props,'onPageChange') && _.isFunction(this.props.onPageChange)){
      this.props.onPageChange(page);
      return;
    }
    if(this.items.length>this.pageSize){
      this.setState({page: page});
    }
  }

  renderPaginator() {
    // validations
    if(this.items.length === 0) return null;
    if(this.total <= 0) return null;
    if(this.total < this.pageSize) return null;
    let page = this.state.page;
    // populate vars
    let to = page * this.pageSize;
    let from = to - this.pageSize + 1;
    if (to > this.total) to = this.total;
    // done render pager
    return (
      <div style={{ display: "flex", justifyContent: "center", marginTop: "30px" }}>
        <Pagination
          className="ant-pagination"
          onChange={this.onChangePage}
          current={page}
          locale={this.labelButtons}
          showTotal={total => `${from} - ${to} de ${this.total} registros`}
          total={this.total}
          pageSize={this.pageSize}
        />
      </div>
    );
  }

  renderBlank(mensaje) {
    return (
      <TableRow key={1}>
        <TableRowColumn><p>{mensaje}</p></TableRowColumn>
      </TableRow>
    )
  }

  renderTableBody(rows) {
    const context = this;
    if(rows.length === 0){
      return this.renderBlank(this.labelNoData);
    }
    // row loop
    return rows.map( (row, index) => {
      let arrData = [], c=0;
      // columns loop
      for(let key in this.props.display){
        // fist iteration?
        let checkbox = null;
        if(c===0){
          const id = row[this.checkId];
          const checked = this.state.selectedItems[id]? true : false;
          checkbox = this.renderCheckItem(id, checked);
          arrData.push(<TableRowColumn key={c++} style={{width:'20px'}}>{checkbox}</TableRowColumn>)
        }
        try {
          let display = this.props.display[key];
          const style = _.isObject(display) && _.has(display,'style')? Object.assign({}, display.style) : {};
          let htmlTd = null;
          if(_.isString(display)){
            htmlTd = row[key];
          } else if(_.isObject(display) && _.has(display,'render') && _.isFunction(display['render'])){
            htmlTd = display.render(row, context);
          }
          if(htmlTd!==null){
            arrData.push(<TableRowColumn key={c++} style={style}>{htmlTd}</TableRowColumn>)
          }
        } catch(error) {
          arrData.push(<TableRowColumn key={c++}>error</TableRowColumn>)
          console.log(`<CheckListTable> Error en el campo ${key}.`, error.toString(), row);
        }
      }
      // done with this single row...
      return (<TableRow key={index} style={{ height: '16px', fontSize: '12px' }}>{arrData}</TableRow>)
    })
  }

  renderTableHeaders(display){
    let arrHeaders = [];
    let c=0;
    for(let i in display){
      const item = display[i];
      if(c===0){
        const checked = this.state.selectAllItems;
        const checkbox = this.renderCheckItem('all', checked);
        arrHeaders.push(<TableRowColumn key={c++} style={{width:'20px'}}>{checkbox}</TableRowColumn>)
      }
      const style = _.isObject(item) && _.has(item,'style')? Object.assign({}, item.style) : {};
      let htmlTh = null;
      if(_.isString(item)){
        htmlTh = item;
      } else if(_.isObject(item) && _.has(item, 'header')) {
        htmlTh =item.header;
      }
      if(htmlTh!==null){
        arrHeaders.push(<TableHeaderColumn key={i} style={style}>{htmlTh}</TableHeaderColumn>)
      }
    }
    return(<TableRow>{arrHeaders}</TableRow>);
  }

  render() {
    // init vars
    let contenido, header, paginator, rows;
    let { grid, display } = this.props;
    header = this.renderTableHeaders(display);
		if(grid.fetchingData){
			contenido = this.renderBlank(this.labelLoading);
		} else {
      rows = this.getPageRows();
			contenido = this.renderTableBody(rows);
      paginator = this.renderPaginator();
		}
    // done, render all
    return (
      <div>
        <Table selectable={false}>
          <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
              {header}
          </TableHeader>
          <TableBody stripedRows displayRowCheckbox={false} style={{paddingLeft: 500}}>
  					   {contenido}
  				</TableBody>
        </Table>
        {paginator}
      </div>
    )
  }

}

export default CheckListTable



// WEBPACK FOOTER //
// ./src/components/CheckList/CheckListTable.js